import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  Rarity,
  So5LeaderboardRarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CompletedCta } from '@sorare/core/src/components/lobby/LineupActions/CompletedCta';
import { ComposeCta } from '@sorare/core/src/components/lobby/LineupActions/ComposeCta';
import { DeleteCta } from '@sorare/core/src/components/lobby/LineupActions/DeleteCta';
import { EditCta } from '@sorare/core/src/components/lobby/LineupActions/EditCta';
import { LineupsCta } from '@sorare/core/src/components/lobby/LineupActions/LineupsCta';
import { LockedCta } from '@sorare/core/src/components/lobby/LineupActions/LockedCta';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useEvents } from 'hooks/useEvents';

type Props = {
  isUserEligible: boolean;
  isCompleted?: boolean;
  leaderboardRarity: So5LeaderboardRarity;
  nbOfLineups: number;
  lineupId?: string;
  editUrl: string;
  composeUrl: string;
  onDelete: (id: string) => void;
  unlockLink?: string;
  isTraining?: boolean;
  lineupsUrl?: string;
  leaderboardDisplayName?: string;
  teamsCap?: number | null;
};

const Wrapper = styled.aside`
  display: flex;
  gap: var(--half-unit);
  justify-content: center;
  @media ${mobileAndAbove} {
    justify-content: flex-end;
  }
`;

const Inline = styled.span`
  display: flex;
  gap: var(--unit);
  align-items: center;
`;

const CARDS_NEEDED = {
  [So5LeaderboardRarity.COMMON]: Rarity.common,
  [So5LeaderboardRarity.LIMITED]: Rarity.limited,
  [So5LeaderboardRarity.RARE]: Rarity.rare,
  [So5LeaderboardRarity.SUPER_RARE]: Rarity.super_rare,
  [So5LeaderboardRarity.UNIQUE]: Rarity.unique,
  [So5LeaderboardRarity.MIX]: null,
  [So5LeaderboardRarity.RARE_PRO]: undefined,
} as const;

export const LineupActions = ({
  isUserEligible,
  isCompleted = false,
  leaderboardRarity,
  nbOfLineups,
  lineupId,
  editUrl,
  composeUrl,
  onDelete,
  unlockLink,
  lineupsUrl,
  isTraining = false,
  leaderboardDisplayName = '',
  teamsCap,
}: Props) => {
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();
  const track = useEvents({ sport });
  const scarcityNeeded = CARDS_NEEDED[leaderboardRarity];
  const { up: isTablet } = useScreenSize('tablet');

  if (isCompleted) {
    return (
      <CompletedCta
        reason={formatMessage(
          {
            id: 'LobbyUpcoming.UpcomingLeaderboards.beginnerAllCompleted',
            defaultMessage: `Congrats on completing {leaderboard}!`,
          },
          {
            leaderboard: leaderboardDisplayName,
          }
        )}
      />
    );
  }

  if (!isUserEligible) {
    // TODO: Clean this logic up once progression leaderboards have been finalized
    if (leaderboardDisplayName.match(/minors/i)) {
      return (
        <LockedCta
          value={false}
          reason={formatMessage({
            id: 'LobbyUpcoming.UpcomingLeaderboards.minorsLockedReason',
            defaultMessage: 'Unlock the tournament after your first Game Week',
          })}
        />
      );
    }

    if (unlockLink) {
      if (leaderboardRarity === So5LeaderboardRarity.COMMON) {
        return (
          <Button
            color="tertiary"
            size="medium"
            to={unlockLink}
            onClick={() =>
              track('Redirect To Marketplace', {
                source: 'Unlock Competition Dialog CTA',
              })
            }
          >
            <Inline>
              <FormattedMessage
                id="LineupActions.unlock"
                defaultMessage="Unlock"
              />
            </Inline>
          </Button>
        );
      }
      if (!isTablet) {
        return null;
      }
      return (
        <Button
          color="tertiary"
          size="medium"
          to={unlockLink}
          onClick={() =>
            track('Redirect To Marketplace', {
              source: 'Unlock Competition Dialog CTA',
            })
          }
        >
          <Inline>
            <FormattedMessage
              id="LineupActions.getCards"
              defaultMessage="Get {scarcity}Cards"
              values={{
                scarcity: scarcityNeeded
                  ? `${formatMessage(scarcityMessages[scarcityNeeded])} `
                  : '',
              }}
            />
          </Inline>
        </Button>
      );
    }
    return (
      <LockedCta
        value={false}
        reason={formatMessage({
          id: 'LobbyUpcoming.UpcomingLeaderboards.lockedReason',
          defaultMessage:
            "You don't have the required Cards to enter this competition",
        })}
      />
    );
  }

  const canEdit = nbOfLineups > 0 && lineupId && !isTraining;

  if (isTraining) {
    return (
      <Wrapper>
        <LineupsCta
          nb={nbOfLineups}
          teamsCap={teamsCap}
          url={lineupsUrl || composeUrl}
        />
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      {canEdit ? (
        <>
          <DeleteCta onDelete={() => onDelete(lineupId)} showLoader={false} />
          <EditCta
            editUrl={editUrl}
            onTrack={() => track('Click Edit Lineup')}
          />
        </>
      ) : (
        <ComposeCta
          url={composeUrl}
          onTrack={() => track('Click Compose Lineup')}
        />
      )}
    </Wrapper>
  );
};
